import React, { useEffect, useState } from "react";
import SpinnerButton from "@/components/SpinnerButton";

const TYPES = ["Email", "WhatsApp", "Appointment", "Walk-in"];
const SOURCES = [
  "WhatsApp direct", "Instagram DM", "Phone",
  "Email direct", "Referral", "Walk-in", "Other",
];

/**
 * Reusable lead-entry form shared by /staff/log and /admin/leads "Add Lead".
 *
 * Props:
 *   onSubmit(payload) -> Promise. Should resolve on success, throw on error.
 *   teamNames         Optional. When provided, renders a "Duty person"
 *                     dropdown (admin use). Staff calls omit this — the
 *                     backend forces Duty_Person = signed-in staff.
 *   defaultDuty       Optional default selection for the duty dropdown.
 *   onCancel          Optional. Renders a Cancel button when present.
 *   submitLabel       Override submit-button text (default "Log lead").
 */
export default function ManualLeadForm({
  onSubmit, teamNames = null, defaultDuty = "", onCancel = null,
  submitLabel = "Log lead",
}) {
  const [form, setForm] = useState({
    type: "WhatsApp",
    source: "WhatsApp direct",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    product: "",
    notes: "",
    appointment_date: "",
    appointment_time: "",
    duty_person: defaultDuty,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Keep duty_person in sync if the caller changes defaultDuty (e.g.
    // admin form fetches today's duty asynchronously).
    if (defaultDuty && !form.duty_person) {
      setForm((f) => ({ ...f, duty_person: defaultDuty }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDuty]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Could not log lead.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isAppt = form.type === "Appointment";

  return (
    <form onSubmit={submit} data-testid="manual-lead-form">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
        <div>
          <label className="scw-label">Type</label>
          <select className="scw-select" required value={form.type} onChange={set("type")} data-testid="manual-lead-type">
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="scw-label">Source</label>
          <select className="scw-select" required value={form.source} onChange={set("source")} data-testid="manual-lead-source">
            {SOURCES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 18 }}>
        <label className="scw-label">Customer name</label>
        <input className="scw-input" required value={form.customer_name} onChange={set("customer_name")} data-testid="manual-lead-name" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
        <div>
          <label className="scw-label">Phone</label>
          <input className="scw-input" required value={form.customer_phone} onChange={set("customer_phone")} placeholder="+60 / +44 / etc" data-testid="manual-lead-phone" />
        </div>
        <div>
          <label className="scw-label">Email (optional)</label>
          <input className="scw-input" type="email" value={form.customer_email} onChange={set("customer_email")} data-testid="manual-lead-email" />
        </div>
      </div>
      <div style={{ marginBottom: 18 }}>
        <label className="scw-label">Watch / Product of interest (optional)</label>
        <input className="scw-input" value={form.product} onChange={set("product")} placeholder="e.g. Rolex Submariner 124060" data-testid="manual-lead-product" />
      </div>
      <div style={{ marginBottom: 18 }}>
        <label className="scw-label">Initial notes (optional)</label>
        <textarea className="scw-textarea" value={form.notes} onChange={set("notes")} data-testid="manual-lead-notes" />
      </div>
      {isAppt && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
          <div>
            <label className="scw-label">Appointment date</label>
            <input className="scw-input" type="date" required value={form.appointment_date} onChange={set("appointment_date")} data-testid="manual-lead-apptdate" />
          </div>
          <div>
            <label className="scw-label">Appointment time</label>
            <input className="scw-input" type="time" required value={form.appointment_time} onChange={set("appointment_time")} data-testid="manual-lead-appttime" />
          </div>
        </div>
      )}
      {teamNames && (
        <div style={{ marginBottom: 18 }}>
          <label className="scw-label">Duty person (lead owner)</label>
          <select className="scw-select" required value={form.duty_person} onChange={set("duty_person")} data-testid="manual-lead-duty">
            <option value="">{"Select a team member\u2026"}</option>
            {teamNames.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      )}
      {error && <p style={{ color: "#a13b3b", fontSize: 13, marginBottom: 12 }} data-testid="manual-lead-error">{error}</p>}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        {onCancel && (
          <button type="button" className="scw-btn scw-btn-ghost" onClick={onCancel} data-testid="manual-lead-cancel">Cancel</button>
        )}
        <SpinnerButton loading={loading} dataTestId="manual-lead-submit">{submitLabel}</SpinnerButton>
      </div>
    </form>
  );
}
